import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { cn } from '@up2d8/ui';
import { AdminIcon } from './icons';

/**
 * De bouwstenen van een beheerpagina (spec A §3, §4 en §5 "Kaart"): pagina, paginakop, raster en
 * kaart. Alleen klassen en toegankelijkheid; wat erin komt, beslist het scherm.
 */

/** `.a-page`: max. 1240 breed (smal: 880), gecentreerd, `gap: 20px`. */
export function AdminPage({
  narrow,
  className,
  testId,
  children,
}: {
  narrow?: boolean;
  className?: string;
  testId?: string;
  children: ReactNode;
}) {
  return (
    <div className={cn('a-page', narrow && 'narrow', className)} data-testid={testId}>
      {children}
    </div>
  );
}

export interface CrumbLink {
  label: string;
  /** Een route; zonder `to` is het een knop met `onClick`. */
  to?: string;
  params?: Record<string, string>;
  onClick?: () => void;
}

/**
 * De terugkoppeling boven de titel (`.a-crumb`): pijl + "Berichten", Poppins 500, 13 px.
 * Een link als er een adres is, anders een knop.
 */
export function Crumb({ crumb }: { crumb: CrumbLink }) {
  const content = (
    <>
      <AdminIcon name="arrowLeft" size={14} />
      {crumb.label}
    </>
  );
  if (crumb.to) {
    return (
      <Link to={crumb.to} params={crumb.params as never} className="a-crumb">
        {content}
      </Link>
    );
  }
  return (
    <button type="button" className="a-crumb" onClick={crumb.onClick}>
      {content}
    </button>
  );
}

/**
 * De paginakop (`.a-pagehead`, spec §4): links terugkoppeling, titel (h1, 24 px) en één zin uitleg;
 * rechts de acties, de primaire als laatste.
 *
 * De titel is de `h1` van de pagina. Gebruik `PageHead` alleen op een pagina die in `ZoneLayout`
 * met `ownHeading` staat; anders staan er twee `h1`'s (de kopbalk en deze).
 */
export function PageHead({
  title,
  sub,
  crumb,
  actions,
  testId,
}: {
  title: ReactNode;
  sub?: ReactNode;
  crumb?: CrumbLink;
  actions?: ReactNode;
  testId?: string;
}) {
  return (
    <div className="a-pagehead" data-testid={testId}>
      <div>
        {crumb ? <Crumb crumb={crumb} /> : null}
        <h1 className="a-title">{title}</h1>
        {sub ? <p className="a-sub">{sub}</p> : null}
      </div>
      {actions ? <div className="a-actions">{actions}</div> : null}
    </div>
  );
}

/** `.a-grid`: `side` = inhoud + rechterkolom van 360 (valt onder 1100 eronder), `two`, `three`. */
export function Grid({
  variant,
  className,
  children,
}: {
  variant: 'side' | 'two' | 'three';
  className?: string;
  children: ReactNode;
}) {
  return <div className={cn('a-grid', variant, className)}>{children}</div>;
}

/** `.a-stack`: een kolom met `gap: 16px`, bv. de rechterkolom. */
export function Stack({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cn('a-stack', className)}>{children}</div>;
}

/** `.a-card`: wit, rand `#EEF1F5`, radius 18, schaduw sm. Standaard een `<section>`. */
export function Card({
  as: Tag = 'section',
  className,
  testId,
  label,
  children,
}: {
  as?: 'section' | 'div' | 'article';
  className?: string;
  testId?: string;
  /** Een naam voor de sectie als er geen `CardHead` met titel in staat. */
  label?: string;
  children: ReactNode;
}) {
  return (
    <Tag className={cn('a-card', className)} data-testid={testId} aria-label={label}>
      {children}
    </Tag>
  );
}

/** `.a-count`: teller naast een titel; `attention` = oranje met wit. */
export function Count({ attention, children }: { attention?: boolean; children: ReactNode }) {
  return <span className={cn('a-count', attention && 'attention')}>{children}</span>;
}

/**
 * De kop van een kaart (`.a-card-head`): titel (Poppins 600, 15 px) met optionele teller, rechts
 * eventueel een link of knop.
 */
export function CardHead({
  title,
  count,
  countAttention,
  actions,
  level = 2,
  id,
}: {
  title: ReactNode;
  count?: ReactNode;
  countAttention?: boolean;
  actions?: ReactNode;
  /** Het kopniveau; 2 onder de `h1` van de pagina, 3 in een kaart binnen een kaart. */
  level?: 2 | 3;
  id?: string;
}) {
  const Heading = level === 2 ? 'h2' : 'h3';
  return (
    <div className="a-card-head">
      <Heading className="a-card-title" id={id}>
        {title}
        {count !== undefined && count !== null ? (
          <Count attention={countAttention}>{count}</Count>
        ) : null}
      </Heading>
      {actions ? <div className="a-actions">{actions}</div> : null}
    </div>
  );
}

/** `.a-card-pad`: inhoud met padding 20 en `gap: 14px`. */
export function CardPad({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cn('a-card-pad', className)}>{children}</div>;
}

/** `.a-card-foot`: links tekst ("12 van 52 getoond"), rechts een knop ("Meer laden"). */
export function CardFoot({ children }: { children: ReactNode }) {
  return <div className="a-card-foot">{children}</div>;
}

/** `.a-sect`: een sectie in een paneel of venster, met `.a-sect-title` (12 px, hoofdletters). */
export function Sect({
  title,
  className,
  children,
}: {
  title: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    <section className={cn('a-sect', className)}>
      <h3 className="a-sect-title">{title}</h3>
      {children}
    </section>
  );
}

/** `.a-dl`: twee kolommen, term (160 breed, `#4B5563`) en waarde. */
export function DefinitionList({ items }: { items: [ReactNode, ReactNode][] }) {
  return (
    <dl className="a-dl">
      {items.map(([term, value], index) => (
        <div key={index} style={{ display: 'contents' }}>
          <dt>{term}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}
